/**
 * Cliente HTTP do frontend. Todas as chamadas passam pelo backend
 * (que fala com o OpenCode) — o navegador nunca vê credenciais do servidor.
 */
import type {
  AgentInfo,
  AppState,
  CommandInfo,
  ConsumeProviderResetCreditResult,
  DeleteSessionResult,
  FileContent,
  FileNode,
  FileStatusEntry,
  FormatterStatus,
  LspStatus,
  OcipEvent,
  PermissionRequest,
  ProjectEntry,
  Provider,
  ProviderAuthFlow,
  ProviderAuthMethodInfo,
  ProviderResetCredits,
  ProviderUsageInfo,
  QuestionRequest,
  Session,
  SessionMessage,
  SessionPrefs,
  SessionStatusInfo,
  SkillInfo,
  TodoItem,
  UpdateStatus,
  VcsFileDiff,
  VcsFileStatusEntry,
  VcsInfo,
} from "./types";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("X-Requested-With", "XMLHttpRequest");
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const res = await fetch(path, { ...init, headers, credentials: "same-origin" });
  if (!res.ok) {
    let message = res.status === 401 ? "Sessão expirada. Faça login novamente." : `Erro ${res.status}`;
    let detail: unknown;
    try {
      const data = (await res.json()) as { error?: string; detail?: unknown };
      if (data.error) message = data.error;
      detail = data.detail;
    } catch {
      /* corpo não-JSON */
    }
    throw new ApiError(message, res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function q(params: Record<string, string | number | boolean | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export interface PromptPayload {
  text: string;
  files?: Array<{ uri: string; name?: string; description?: string }>;
  model?: { providerID: string; modelID: string };
  agent?: string;
}

export const api = {
  // Autenticação
  login: (username: string, password: string) =>
    request<{ user: string }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  logout: () => request<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
  me: () => request<{ user: string }>("/api/auth/me"),

  // Saúde e estado
  health: () => request<{ ok: boolean; opencode: { ok: boolean } }>(`/api/health`),

  // Atualização automática do motor OpenCode
  updateStatus: () => request<UpdateStatus>("/api/update/status"),
  updateCheck: () => request<UpdateStatus>("/api/update/check", { method: "POST" }),
  updateRun: () => request<UpdateStatus & { queued: boolean }>("/api/update/run", { method: "POST" }),
  updateSettings: (patch: { autoUpdate?: boolean; intervalHours?: number }) =>
    request<{ ok: boolean; control: { autoUpdate: boolean; intervalHours: number } }>("/api/update/settings", {
      method: "POST",
      body: JSON.stringify(patch),
    }),
  getState: () => request<AppState>("/api/state"),
  putState: (patch: Partial<AppState>) =>
    request<AppState>("/api/state", { method: "PUT", body: JSON.stringify(patch) }),

  // Projetos
  projects: () => request<{ allowed: ProjectEntry[]; opencode: unknown[] }>("/api/projects"),

  // Sessões
  sessions: (directory: string, search?: string) =>
    request<Session[]>(`/api/sessions${q({ directory, search })}`),
  sessionStatus: (directory: string) =>
    request<Record<string, SessionStatusInfo>>(`/api/session-status${q({ directory })}`),
  createSession: (
    directory: string,
    body: { title?: string; parentID?: string; agent?: string; model?: { id: string; providerID: string }; allowAllPermissions?: boolean },
  ) => request<Session>(`/api/sessions${q({ directory })}`, { method: "POST", body: JSON.stringify(body) }),
  session: (directory: string, id: string) => request<Session>(`/api/sessions/${id}${q({ directory })}`),
  updateSession: (directory: string, id: string, body: { title?: string }) =>
    request<Session>(`/api/sessions/${id}${q({ directory })}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteSession: (directory: string, id: string) =>
    request<DeleteSessionResult>(`/api/sessions/${id}${q({ directory })}`, { method: "DELETE" }),
  sessionPrefs: (directory: string, id: string) =>
    request<SessionPrefs>(`/api/sessions/${id}/prefs${q({ directory })}`),
  putSessionPrefs: (directory: string, id: string, patch: { model?: { providerID: string; modelID: string }; allowAppRun?: boolean; allowAllPermissions?: boolean }) =>
    request<SessionPrefs>(`/api/sessions/${id}/prefs${q({ directory })}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),
  sessionFiles: (directory: string, id: string) =>
    request<{ files: string[]; skipped: number }>(`/api/sessions/${id}/files${q({ directory })}`),
  messages: (directory: string, id: string) =>
    request<SessionMessage[]>(`/api/sessions/${id}/messages${q({ directory })}`),
  prompt: (directory: string, id: string, body: PromptPayload) =>
    request<unknown>(`/api/sessions/${id}/prompt${q({ directory })}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  abort: (directory: string, id: string) =>
    request<boolean>(`/api/sessions/${id}/abort${q({ directory })}`, { method: "POST", body: "{}" }),
  fork: (directory: string, id: string, messageID?: string) =>
    request<Session>(`/api/sessions/${id}/fork${q({ directory })}`, {
      method: "POST",
      body: JSON.stringify({ messageID }),
    }),
  children: (directory: string, id: string) =>
    request<Session[]>(`/api/sessions/${id}/children${q({ directory })}`),
  todos: (directory: string, id: string) =>
    request<TodoItem[]>(`/api/sessions/${id}/todos${q({ directory })}`),
  sessionDiff: (directory: string, id: string, messageID?: string) =>
    request<VcsFileDiff[]>(`/api/sessions/${id}/diff${q({ directory, messageID })}`),
  summarize: (directory: string, id: string, model: { providerID: string; modelID: string }) =>
    request<boolean>(`/api/sessions/${id}/summarize${q({ directory })}`, {
      method: "POST",
      body: JSON.stringify(model),
    }),
  share: (directory: string, id: string) =>
    request<Session>(`/api/sessions/${id}/share${q({ directory })}`, { method: "POST", body: "{}" }),
  unshare: (directory: string, id: string) =>
    request<Session>(`/api/sessions/${id}/share${q({ directory })}`, { method: "DELETE" }),
  revert: (directory: string, id: string, messageID: string, partID?: string) =>
    request<Session>(`/api/sessions/${id}/revert${q({ directory })}`, {
      method: "POST",
      body: JSON.stringify({ messageID, partID }),
    }),
  unrevert: (directory: string, id: string) =>
    request<Session>(`/api/sessions/${id}/unrevert${q({ directory })}`, { method: "POST", body: "{}" }),
  initSession: (directory: string, id: string, body: { modelID: string; providerID: string; messageID: string }) =>
    request<boolean>(`/api/sessions/${id}/init${q({ directory })}`, { method: "POST", body: JSON.stringify(body) }),
  command: (directory: string, id: string, body: { command: string; arguments?: string; agent?: string }) =>
    request<SessionMessage>(`/api/sessions/${id}/command${q({ directory })}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  shell: (directory: string, id: string, body: { command: string; agent?: string }) =>
    request<SessionMessage>(`/api/sessions/${id}/shell${q({ directory })}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Permissões e perguntas
  permissions: (directory: string) => request<PermissionRequest[]>(`/api/permissions${q({ directory })}`),
  replyPermission: (directory: string, id: string, reply: "once" | "always" | "reject", message?: string) =>
    request<boolean>(`/api/permissions/${id}/reply${q({ directory })}`, {
      method: "POST",
      body: JSON.stringify({ reply, message }),
    }),
  questions: (directory: string) => request<QuestionRequest[]>(`/api/questions${q({ directory })}`),
  replyQuestion: (directory: string, id: string, answers: string[][]) =>
    request<boolean>(`/api/questions/${id}/reply${q({ directory })}`, {
      method: "POST",
      body: JSON.stringify({ answers }),
    }),
  rejectQuestion: (directory: string, id: string) =>
    request<boolean>(`/api/questions/${id}/reject${q({ directory })}`, { method: "POST", body: "{}" }),

  // Provedores / agentes / comandos
  providers: (directory: string) =>
    request<{ all: Provider[]; default: Record<string, string>; connected: string[] }>(
      `/api/providers${q({ directory })}`,
    ),
  setProviderAuth: (providerID: string, key: string) =>
    request<{ ok: boolean; connected: string[] }>(
      `/api/providers/${encodeURIComponent(providerID)}/auth`,
      { method: "POST", body: JSON.stringify({ key }) },
    ),
  removeProviderAuth: (providerID: string) =>
    request<{ ok: boolean; connected: string[] }>(
      `/api/providers/${encodeURIComponent(providerID)}/auth`,
      { method: "DELETE" },
    ),
  providerAuthMethods: (directory: string) =>
    request<Record<string, ProviderAuthMethodInfo[]>>(`/api/providers/auth${q({ directory })}`),
  providerOauthStart: (providerID: string, method: number) =>
    request<ProviderAuthFlow>(`/api/providers/${encodeURIComponent(providerID)}/oauth/start`, {
      method: "POST",
      body: JSON.stringify({ method }),
    }),
  providerOauthComplete: (providerID: string, method: number, code?: string) =>
    request<{ ok: boolean; pending?: boolean; message?: string; connected: string[] }>(
      `/api/providers/${encodeURIComponent(providerID)}/oauth/complete`,
      { method: "POST", body: JSON.stringify({ method, code }) },
    ),
  providerUsage: (providerID: string) =>
    request<ProviderUsageInfo>(`/api/providers/${encodeURIComponent(providerID)}/usage`),
  providerResetCredits: (providerID: string) =>
    request<ProviderResetCredits>(`/api/providers/${encodeURIComponent(providerID)}/usage/reset-credits`),
  consumeProviderResetCredit: (
    providerID: string,
    body: { idempotencyKey: string; creditId?: string; accountKey: string },
  ) => request<ConsumeProviderResetCreditResult>(
    `/api/providers/${encodeURIComponent(providerID)}/usage/reset-credits/consume`,
    { method: "POST", body: JSON.stringify(body) },
  ),
  agents: (directory: string) => request<AgentInfo[]>(`/api/agents${q({ directory })}`),
  commands: (directory: string) => request<CommandInfo[]>(`/api/commands${q({ directory })}`),

  // Configuração e extensões
  config: (directory: string) => request<Record<string, unknown>>(`/api/config${q({ directory })}`),
  updateConfig: (directory: string, body: unknown) =>
    request<Record<string, unknown>>(`/api/config${q({ directory })}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  mcp: (directory: string) => request<Record<string, unknown>>(`/api/mcp${q({ directory })}`),
  mcpConnect: (directory: string, name: string) =>
    request<unknown>(`/api/mcp/${encodeURIComponent(name)}/connect${q({ directory })}`, {
      method: "POST",
      body: "{}",
    }),
  mcpDisconnect: (directory: string, name: string) =>
    request<unknown>(`/api/mcp/${encodeURIComponent(name)}/disconnect${q({ directory })}`, {
      method: "POST",
      body: "{}",
    }),
  skills: (directory: string) => request<SkillInfo[]>(`/api/skills${q({ directory })}`),
  lsp: (directory: string) => request<LspStatus[]>(`/api/lsp${q({ directory })}`),
  formatter: (directory: string) => request<FormatterStatus[]>(`/api/formatter${q({ directory })}`),

  // Arquivos e busca
  files: (directory: string, path?: string) =>
    request<FileNode[]>(`/api/files${q({ directory, path })}`),
  file: (directory: string, path: string) => request<FileContent>(`/api/file${q({ directory, path })}`),
  fileStatus: (directory: string) => request<FileStatusEntry[]>(`/api/file-status${q({ directory })}`),
  search: (directory: string, pattern: string) =>
    request<Array<{ path: { text: string }; lines: { text: string } }>>(`/api/search${q({ directory, pattern })}`),
  searchFiles: (directory: string, query: string) =>
    request<string[]>(`/api/search/files${q({ directory, query })}`),

  // VCS
  vcs: (directory: string) => request<VcsInfo>(`/api/vcs${q({ directory })}`),
  vcsStatus: (directory: string) =>
    request<VcsFileStatusEntry[]>(`/api/vcs/status${q({ directory })}`),
  vcsDiff: (directory: string) => request<VcsFileDiff[]>(`/api/vcs/diff${q({ directory })}`),

  // Uploads
  upload: async (file: File): Promise<{ path: string; uri: string; name: string; size: number }> => {
    const res = await fetch("/api/uploads", {
      method: "POST",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "X-Filename": encodeURIComponent(file.name),
        "Content-Type": file.type || "application/octet-stream",
      },
      credentials: "same-origin",
      body: file,
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(data.error ?? `Erro ${res.status} no upload`, res.status);
    }
    return (await res.json()) as { path: string; uri: string; name: string; size: number };
  },
};

/** Assina o stream de eventos do OpenCode repassado pelo backend. */
export function openEventStream(directory: string, onEvent: (ev: OcipEvent) => void): () => void {
  const es = new EventSource(`/api/events${q({ directory })}`);
  es.onmessage = (msg) => {
    try {
      const parsed = JSON.parse(msg.data) as OcipEvent;
      if (parsed && typeof parsed.type === "string") onEvent(parsed);
    } catch {
      /* frame de heartbeat/keepalive */
    }
  };
  return () => es.close();
}
